import { createSlice, Slice } from '@reduxjs/toolkit';
import { IPaginatedData } from 'modules/shared/interfaces/paginationMeta';
import { IReqStatus } from 'modules/shared/interfaces/requestStatus';
import { RootStateOrAny } from 'react-redux';
import { IQuestionDetails, IQuestionListItem } from '../interfaces/question';

export const ONBOARDING_QUESTION_KEY_IN_STORE = 'question';

export interface IOnboardingQuestionsState {
	isLoading: boolean;
	questions: IPaginatedData<IQuestionListItem>;
	deleteQuestion: IReqStatus;
	addQuestion: IReqStatus;
	updateQuestion: {
		itemToUpdate: {
			isLoading: boolean;
			data?: IQuestionDetails;
			status: IReqStatus;
		};
		status: IReqStatus;
	};
}

const initialState: IOnboardingQuestionsState = {
	questions: {
		items: [],
		meta: {
			currentPage: 1,
			itemsPerPage: 5,
			itemCount: 2,
			totalItems: 2,
			totalPages: 1,
		},
	},
	isLoading: false,
	addQuestion: {
		success: false,
		error: false,
	},
	deleteQuestion: {
		success: false,
		error: false,
	},
	updateQuestion: {
		itemToUpdate: {
			isLoading: false,
			data: undefined,
			status: {
				success: false,
				error: false,
			},
		},
		status: {
			success: false,
			error: false,
		},
	},
};

const questionSlice: Slice = createSlice({
	name: ONBOARDING_QUESTION_KEY_IN_STORE,
	initialState,
	reducers: {
		getAllQuestions: state => {
			state.isLoading = true;
		},
		getAllQuestionsSuccess: (state, action) => {
			state.questions = action.payload;
			state.isLoading = false;
		},
		getAllQuestionsFailure: state => {
			state.isLoading = false;
		},
		deleteQuestion: state => {
			state.deleteQuestion = {
				success: false,
				error: false,
			};
			state.isLoading = true;
		},
		deleteQuestionSuccess: (state: RootStateOrAny) => {
			state.deleteQuestion = {
				success: true,
				error: false,
			};
			state.isLoading = false;
		},
		deleteQuestionFailure: (state: RootStateOrAny) => {
			state.deleteQuestion = {
				success: false,
				error: true,
			};
			state.isLoading = false;
		},
		resetDeleteQuestionStatus: state => {
			state.deleteQuestion = {
				success: false,
				error: false,
			};
		},
		addQuestion: state => {
			state.isLoading = true;
			state.addQuestion = {
				success: false,
				error: false,
			};
		},
		addQuestionSuccess: (state: RootStateOrAny) => {
			state.addQuestion = {
				success: true,
				error: false,
			};
			state.isLoading = false;
		},
		addQuestionFailure: state => {
			state.addQuestion = {
				success: false,
				error: true,
			};
			state.isLoading = false;
		},
		getQuestionToUpdate: state => {
			state.updateQuestion.itemToUpdate = {
				isLoading: true,
				data: undefined,
				status: {
					success: false,
					error: false,
				},
			};
		},
		getQuestionToUpdateSuccess: (state, action) => {
			state.updateQuestion.itemToUpdate = {
				isLoading: false,
				data: action.payload,
				status: {
					success: true,
					error: false,
				},
			};
		},
		getQuestionToUpdateFailure: state => {
			state.updateQuestion.itemToUpdate = {
				isLoading: false,
				data: undefined,
				status: {
					success: false,
					error: true,
				},
			};
		},
		updateQuestion: state => {
			state.isLoading = true;
			state.updateQuestion.status = {
				success: false,
				error: false,
			};
		},
		updateQuestionSuccess: state => {
			state.isLoading = false;
			state.updateQuestion.status = {
				success: true,
				error: false,
			};
		},
		updateQuestionFailure: state => {
			state.isLoading = false;
			state.updateQuestion.status = {
				success: false,
				error: true,
			};
		},
		resetQuestionData: () => {
			return initialState;
		},
	},
});

export const {
	getAllQuestions,
	getAllQuestionsSuccess,
	getAllQuestionsFailure,
	deleteQuestion,
	deleteQuestionSuccess,
	deleteQuestionFailure,
	resetDeleteQuestionStatus,
	addQuestion,
	addQuestionSuccess,
	addQuestionFailure,
	resetQuestionData,
	getQuestionToUpdate,
	getQuestionToUpdateSuccess,
	getQuestionToUpdateFailure,
	updateQuestion,
	updateQuestionSuccess,
	updateQuestionFailure,
} = questionSlice.actions;

export const onboardingQuestionStateSelector = (
	state: RootStateOrAny,
): IOnboardingQuestionsState => state[ONBOARDING_QUESTION_KEY_IN_STORE];

export default questionSlice;
